<template>
    <v-layout wrap v-if="server && chargerName && numberPlugs">
        <v-flex xs12 class="text-right">
            <strong>OCPP 1.6: </strong>
            <v-btn x-small color="error" class="ma-1 white--text" @click="reset('Hard')"> Hard Reset </v-btn>
            <v-btn x-small color="error" class="ma-1 white--text" @click="reset('Soft')"> Soft Reset </v-btn>
            <v-btn x-small color="error" class="ma-1 white--text" @click="closeSocket()"> Fechar Socket </v-btn>
        </v-flex>
        <v-flex xs12 class="text-right" v-for="index in numberPlugs" :key="index">
            <strong>Plug {{ index }}: </strong>
            <v-btn x-small color="error" class="ma-1 white--text" @click="remoteStart(index)"> RemoteStart </v-btn>
            <v-btn x-small color="error" class="ma-1 white--text" @click="remoteStop(index)"> RemoteStop </v-btn>
            <v-btn x-small color="error" class="ma-1 white--text" @click="changeAvailability('Inoperative', index)"> Inoperative </v-btn>
            <v-btn x-small color="error" class="ma-1 white--text" @click="changeAvailability('Operative', index)"> Operative </v-btn>
            <v-btn x-small color="error" class="ma-1 white--text" @click="unlockConnector(index)"> Unlock Connector </v-btn>
            <v-btn x-small color="error" class="ma-1 white--text" @click="setProfile(index)"> Set Profile </v-btn>
            <v-btn x-small color="error" class="ma-1 white--text" @click="cancelProfile(index)"> Cancel Profile </v-btn>
            <v-btn x-small color="error" class="ma-1 white--text" @click="triggerMessage(index, 'MeterValues')"> MeterValues </v-btn>
            <v-btn x-small color="error" class="ma-1 white--text" @click="triggerMessage(index, 'StatusNotification')"> StatusNotification </v-btn>
        </v-flex>

        <v-flex xs12 class="text-right" v-if="configure">
            <v-alert color="primary" outlined dense>
                <strong>Configuração: </strong>
                <v-row align="center">
                    <v-col cols="6">
                        <v-select label="Key" v-model="key" :items="configurationKeys" item-text="name" item-value="key" outlined dense>
                            <template v-slot:item="{ item }">
                                <div>
                                    <div class="font-weight-bold">{{ item.name }}</div>
                                    <div class="caption text--secondary">{{ item.description }}</div>
                                </div>
                            </template>
                        </v-select>
                    </v-col>
                    <v-col cols="6">
                        <v-text-field label="Value" v-model="value" />
                    </v-col>
                </v-row>
                <v-btn small color="success" class="ma-1 white--text" @click="getConfiguration"> Buscar </v-btn>
                <v-btn small color="error" class="ma-1 white--text" @click="changeConfiguration"> Enviar </v-btn>
                <p class="my-1 my-sm-2" v-html="response" />
            </v-alert>
        </v-flex>

        <ConfirmModal ref="confirm" />

        <v-dialog v-model="profileDialog" max-width="400" persistent>
            <v-card>
                <v-card-title class="headline primary white--text">Set Charging Profile</v-card-title>
                <v-card-text class="mt-4">
                    <v-text-field
                        label="Power (kW)"
                        v-model.number="profilePower"
                        type="number"
                        min="0"
                        max="200"
                        outlined
                        dense
                    />
                    <v-text-field
                        label="Time (s)"
                        v-model.number="profileTime"
                        type="number"
                        min="0"
                        outlined
                        dense
                    />
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="success" @click="confirmSetProfile">
                        <v-icon dark left>check_circle</v-icon>
                        Confirmar
                    </v-btn>
                    <v-btn color="error" @click="profileDialog = false">
                        <v-icon dark left>cancel</v-icon>
                        Cancelar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-layout>
</template>

<script>
import ConfirmModal from "@/components/core/ConfirmModal";

export default {
    props: {
        chargerName: {
            type: String,
            required: true,
        },
        numberPlugs: {
            type: Number,
            required: true,
        },
        server: {
            type: String,
            required: true,
        },
        configure: {
            type: Boolean,
            default: false,
        },
    },

    components: { ConfirmModal },

    data() {
        return {
            key: "ConnectionTimeOut",
            value: "",
            response: "",
            profileDialog: false,
            profilePlug: null,
            profilePower: 0,
            profileTime: 600,
            configurationKeys: [
                {
                    key: "AllowOfflineTxForUnknownId",
                    name: "AllowOfflineTxForUnknownId",
                    description: "Enable Unknown Offline Authorization (boolean)",
                    type: "boolean"
                },
                {
                    key: "AuthorizationCacheEnabled",
                    name: "AuthorizationCacheEnabled",
                    description: "Enable Authorization Cache (boolean)",
                    type: "boolean"
                },
                {
                    key: "AuthorizeRemoteTxRequests",
                    name: "AuthorizeRemoteTxRequests",
                    description: "Authorize remote start transaction requests (boolean)",
                    type: "boolean"
                },
                {
                    key: "BlinkRepeat",
                    name: "BlinkRepeat",
                    description: "Number of times to blink Charge Point lighting (integer)",
                    type: "integer"
                },
                {
                    key: "ClockAlignedDataInterval",
                    name: "ClockAlignedDataInterval",
                    description: "Size in seconds of clock-aligned data interval (integer)",
                    type: "integer"
                },
                {
                    key: "ConnectionTimeOut",
                    name: "ConnectionTimeOut",
                    description: "Timeout from 'Preparing' until transaction is canceled (seconds)",
                    type: "integer"
                },
                {
                    key: "ConnectorPhaseRotation",
                    name: "ConnectorPhaseRotation",
                    description: "Phase rotation per connector (CSL: NotApplicable, Unknown, RST, RTS, SRT, STR, TRS, TSR)",
                    type: "CSL"
                },
                {
                    key: "ConnectorPhaseRotationMaxLength",
                    name: "ConnectorPhaseRotationMaxLength",
                    description: "Maximum items in ConnectorPhaseRotation key (integer)",
                    type: "integer"
                },
                {
                    key: "GetConfigurationMaxKeys",
                    name: "GetConfigurationMaxKeys",
                    description: "Maximum requested configuration keys in GetConfiguration.req (integer)",
                    type: "integer"
                },
                {
                    key: "HeartbeatInterval",
                    name: "HeartbeatInterval",
                    description: "Interval of inactivity before sending Heartbeat (seconds)",
                    type: "integer"
                },
                {
                    key: "LightIntensity",
                    name: "LightIntensity",
                    description: "Percentage of maximum intensity for Charge Point lighting (%)",
                    type: "integer"
                },
                {
                    key: "LocalAuthorizeOffline",
                    name: "LocalAuthorizeOffline",
                    description: "Start transaction for locally-authorized identifiers when offline (boolean)",
                    type: "boolean"
                },
                {
                    key: "LocalPreAuthorize",
                    name: "LocalPreAuthorize",
                    description: "Start transaction for locally-authorized identifiers without waiting Authorize.conf (boolean)",
                    type: "boolean"
                },
                {
                    key: "MaxEnergyOnInvalidId",
                    name: "MaxEnergyOnInvalidId",
                    description: "Maximum energy delivered when identifier is invalidated (Wh)",
                    type: "integer"
                },
                {
                    key: "MeterValuesAlignedData",
                    name: "MeterValuesAlignedData",
                    description: "Clock-aligned measurands for MeterValues.req PDU (CSL)",
                    type: "CSL"
                },
                {
                    key: "MeterValuesAlignedDataMaxLength",
                    name: "MeterValuesAlignedDataMaxLength",
                    description: "Maximum items in MeterValuesAlignedData key (integer)",
                    type: "integer"
                },
                {
                    key: "MeterValuesSampledData",
                    name: "MeterValuesSampledData",
                    description: "Sampled measurands for MeterValues.req PDU (CSL)",
                    type: "CSL"
                },
                {
                    key: "MeterValuesSampledDataMaxLength",
                    name: "MeterValuesSampledDataMaxLength",
                    description: "Maximum items in MeterValuesSampledData key (integer)",
                    type: "integer"
                },
                {
                    key: "MeterValueSampleInterval",
                    name: "MeterValueSampleInterval",
                    description: "Interval between sampling of metering data (seconds)",
                    type: "integer"
                },
                {
                    key: "MinimumStatusDuration",
                    name: "MinimumStatusDuration",
                    description: "Minimum duration status is stable before StatusNotification.req (seconds)",
                    type: "integer"
                },
                {
                    key: "NumberOfConnectors",
                    name: "NumberOfConnectors",
                    description: "Number of physical charging connectors (integer)",
                    type: "integer"
                },
                {
                    key: "ResetRetries",
                    name: "ResetRetries",
                    description: "Number of times to retry unsuccessful reset (integer)",
                    type: "integer"
                },
                {
                    key: "StopTransactionOnEVSideDisconnect",
                    name: "StopTransactionOnEVSideDisconnect",
                    description: "Stop transaction when cable is unplugged from EV (boolean)",
                    type: "boolean"
                },
                {
                    key: "StopTransactionOnInvalidId",
                    name: "StopTransactionOnInvalidId",
                    description: "Stop transaction on non-Accepted authorization status (boolean)",
                    type: "boolean"
                },
                {
                    key: "StopTxnAlignedData",
                    name: "StopTxnAlignedData",
                    description: "Clock-aligned measurands for StopTransaction.req (CSL)",
                    type: "CSL"
                },
                {
                    key: "StopTxnAlignedDataMaxLength",
                    name: "StopTxnAlignedDataMaxLength",
                    description: "Maximum items in StopTxnAlignedData key (integer)",
                    type: "integer"
                },
                {
                    key: "StopTxnSampledData",
                    name: "StopTxnSampledData",
                    description: "Sampled measurands for StopTransaction.req (CSL)",
                    type: "CSL"
                },
                {
                    key: "StopTxnSampledDataMaxLength",
                    name: "StopTxnSampledDataMaxLength",
                    description: "Maximum items in StopTxnSampledData key (integer)",
                    type: "integer"
                },
                {
                    key: "SupportedFeatureProfiles",
                    name: "SupportedFeatureProfiles",
                    description: "List of supported Feature Profiles (CSL: Core, FirmwareManagement, LocalAuthListManagement, Reservation, SmartCharging, RemoteTrigger)",
                    type: "CSL"
                },
                {
                    key: "SupportedFeatureProfilesMaxLength",
                    name: "SupportedFeatureProfilesMaxLength",
                    description: "Maximum items in SupportedFeatureProfiles key (integer)",
                    type: "integer"
                },
                {
                    key: "TransactionMessageAttempts",
                    name: "TransactionMessageAttempts",
                    description: "How often to retry transaction-related messages (integer)",
                    type: "integer"
                },
                {
                    key: "TransactionMessageRetryInterval",
                    name: "TransactionMessageRetryInterval",
                    description: "Wait time before resubmitting transaction-related messages (seconds)",
                    type: "integer"
                },
                {
                    key: "UnlockConnectorOnEVSideDisconnect",
                    name: "UnlockConnectorOnEVSideDisconnect",
                    description: "Unlock cable on Charge Point side when unplugged at EV (boolean)",
                    type: "boolean"
                },
                {
                    key: "WebSocketPingInterval",
                    name: "WebSocketPingInterval",
                    description: "WebSocket ping interval (seconds)",
                    type: "integer"
                }
            ]
        };
    },

    methods: {
        sendCommand(url, parameters) {
            if (this.server) {
                this.$http
                    .postDynamic(this.server, url, parameters)
                    .then((result) => {
                        this.$eventHub.$emit("msgSuccess", result.message ? result.message : "Enviado com sucesso.");
                    }).catch((error) => {
                        this.$eventHub.$emit("msgError", error.message ? error.message : "Erro ao enviar, tente novamente.");
                    });
            } else {
                this.$eventHub.$emit("msgError", "Falta configuração do servidor.");
            }
        },
        confirmAndSent(msg, url, parameters) {
            this.$refs.confirm.open("Confirma?", msg).then((confirm) => {
                if (confirm) this.sendCommand(url, parameters);
            });
        },
        remoteStop(plug) {
            this.confirmAndSent(`Deseja realmente parar o carregamento do plug ${plug}?`, "api/v2/ocpp/remoteStop", { charger: this.chargerName, plug });
        },
        remoteStart(plug) {
            this.confirmAndSent(`Deseja realmente desbloquear o plug ${plug}?`, "api/v2/ocpp/remoteStart", { charger: this.chargerName, plug });
        },
        triggerMessage(plug, requestedMessage) {
            this.confirmAndSent(`Deseja realmente forçar uma requisição de ${requestedMessage} no plug ${plug}?`, "api/v2/ocpp/triggerMessage", { charger: this.chargerName, plug, requestedMessage });
        },
        unlockConnector(plug) {
            this.confirmAndSent(`Deseja realmente desbloquear o plug ${plug} do carregador?`, "api/v2/ocpp/unlockConnector", { charger: this.chargerName, plug: plug });
        },
        changeAvailability(type, plug) {
            this.confirmAndSent("Deseja realmente alterar disponibilidade do carregador?", "api/v2/ocpp/changeAvailability", { charger: this.chargerName, plug: plug, type: type });
        },
        reset(type) {
            this.confirmAndSent("Deseja realmente resetar o carregador?", "api/v2/ocpp/reset", { charger: this.chargerName, type: type });
        },
        closeSocket() {
            this.confirmAndSent("Deseja realmente fechar o socket? Isso vai forçar a reconexão com novas configurações.", "api/v2/ocpp/close", { charger: this.chargerName });
        },
        setProfile(plug) {
            this.profilePlug = plug;
            this.profilePower = 10;
            this.profileTime = null;
            this.profileDialog = true;
        },
        confirmSetProfile() {
            this.profileDialog = false;
            this.confirmAndSent(`Deseja realmente alterar profile do plug ${this.profilePlug}?`, "api/v2/ocpp/setChargingProfile", { charger: this.chargerName, plug: this.profilePlug, power: this.profilePower, time: this.profileTime });
        },
        cancelProfile(plug) {
            this.confirmAndSent(`Deseja realmente cancelar o profile do plug ${plug}?`, "api/v2/ocpp/cancelProfile", { charger: this.chargerName, plug: plug });
        },
        changeConfiguration() {
            if (this.key && this.value) {
                this.confirmAndSent(`Deseja realmente alterar a configuração ${this.key} para ${this.value}?`, "api/v2/ocpp/changeConfiguration", { charger: this.chargerName, key: this.key, value: this.value });
            } else {
                this.$eventHub.$emit("msgError", "Preencha Key e Value.");
            }
        },
        getConfiguration() {
            if (this.key) {
                if (this.server) {
                    this.$http
                        .postDynamic(this.server, "api/v2/ocpp/getConfiguration", { charger: this.chargerName, key: this.key })
                        .then((result) => {
                            if (result) {
                                this.response = result;
                                if (this.response && this.response.configurationKey && this.response.configurationKey.length > 0) {
                                    this.value = this.response.configurationKey[0].value;
                                } else {
                                    this.value = "";
                                }
                                this.$eventHub.$emit("msgSuccess", "Configuração buscada com sucesso.");
                            } else {
                                this.$eventHub.$emit("msgError", "Nenhum valor retornado.");
                            }
                        }).catch((error) => {
                            this.$eventHub.$emit("msgError", error.message ? error.message : "Erro ao enviar, tente novamente.");
                        });
                } else {
                    this.$eventHub.$emit("msgError", "Falta configuração do servidor.");
                }
            } else {
                this.$eventHub.$emit("msgError", "Preencha Key.");
            }
        },
    },

    mounted() {
        this.$eventHub.$on("closeSocketOcpp", () => {
            this.closeSocket();
        });
    },

    beforeDestroy() {
        this.$eventHub.$off("closeSocketOcpp");
    },
};
</script>